import { useCallback } from 'react';

import { useThrottledToast } from '../../hooks/useThrottledToast';

import { Switch, PageHeader, Heading } from '../../shared/ui';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectUser, setUserData } from '../../store/auth.slice';
import { useChangeUserMutation } from '../../services/users.api';
import { useTariffToolCheck } from '../../hooks/useTariffToolCheck';
import { GrowPanel } from '../../components/tools/grow/grow-panel';

const AiGrow = () => {
  const tool = 'grow_ai';
  const isActiveByTariff = useTariffToolCheck(tool);
  const dispatch = useAppDispatch();
  const userData = useAppSelector(selectUser);
  const [changeUser] = useChangeUserMutation();
  const showToast = useThrottledToast();

  const handleCheck = useCallback(
    async (_checked: boolean) => {
      if (!isActiveByTariff || !userData) return;

      const currentTools = userData.active_psy_tools ?? [];
      const nextTools = currentTools.includes(tool)
        ? currentTools.filter((item) => item !== tool)
        : [...currentTools, tool];

      try {
        await changeUser({ id: userData.id, active_psy_tools: nextTools }).unwrap();
        dispatch(setUserData({ ...userData, active_psy_tools: nextTools }));
        showToast('success');
      } catch {
        showToast('error');
      }
    },
    [changeUser, dispatch, isActiveByTariff, showToast, userData],
  );

  return (
    <div className="pb-[85px]">
      <PageHeader backTo="/tools" title="«GROW AI» — шпаргалка по модели GROW" />
      <div className="max-w-[900px] space-y-6 text-ui leading-relaxed text-ink-muted mb-8">
        <section>
          <Heading size="sm" className="mb-2">
            Для чего нужен GROW AI
          </Heading>
          <p>
            GROW AI — интерактивная шпаргалка для психолога, коуча и клиента. Она помогает бережно держать структуру
            разговора: перейти от запроса к понятной цели, увидеть текущую ситуацию, найти варианты и договориться о
            реалистичном следующем шаге. Это не заменяет профессиональное решение специалиста, а поддерживает ход
            диалога вопросами.
          </p>
        </section>

        <section>
          <Heading size="sm" className="mb-2">
            Как устроена модель GROW
          </Heading>
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <strong className="text-ink">Goal — цель.</strong> Уточните, какого результата хочет достичь клиент и по
              каким признакам он поймёт, что движение началось.
            </li>
            <li>
              <strong className="text-ink">Reality — реальность.</strong> Исследуйте текущую ситуацию, уже предпринятые
              попытки, ресурсы и препятствия.
            </li>
            <li>
              <strong className="text-ink">Options — возможности.</strong> Соберите возможные пути, даже если сначала
              они кажутся непривычными или неидеальными.
            </li>
            <li>
              <strong className="text-ink">Way Forward — действия.</strong> Выберите ближайший конкретный шаг, срок и
              способ заметить прогресс.
            </li>
          </ol>
        </section>

        <section>
          <Heading size="sm" className="mb-2">
            Как использовать на сессии
          </Heading>
          <p>
            Откройте нужный этап, выберите подходящий вопрос и следуйте темпу клиента. К вопросам можно возвращаться, а
            этапы отмечать завершёнными по мере разговора. В демо-режиме ответы и отметки существуют только в текущей
            вкладке: после обновления страницы они сбрасываются и не сохраняются в журнале.
          </p>
        </section>
      </div>
      <div className="flex items-center gap-5 mb-[40px]">
        <Switch
          disabled={!isActiveByTariff}
          checked={userData?.active_psy_tools?.includes(tool)}
          onChange={handleCheck}
          label={'Использовать GROW AI в видео-сессиях'}
        />
      </div>

      <section aria-label="Интерактивная шпаргалка GROW" className="max-w-[720px]">
        <Heading size="md" as="h3" className="mb-3">
          Интерактивная шпаргалка
        </Heading>
        <p className="text-ui text-ink-muted mb-4">
          Выберите этап и двигайтесь по вопросам в удобном темпе. Отметки сохраняются только до обновления страницы.
        </p>
        <div className="h-[480px] sm:h-[440px]">
          <GrowPanel embedded />
        </div>
      </section>
    </div>
  );
};

export default AiGrow;
